import React, { useState, useEffect } from 'react'
import './Login.css';
import RingLoader from "react-spinners/RingLoader";
import { Link } from 'react-router-dom';

import { useNavigate } from "react-router-dom";
import PhoneInput from 'react-phone-number-input/input';
import { userExists } from '../../../services/userExists';
import { isValidPhoneNumber } from 'react-phone-number-input';
import posthog from '../../../services/posthog';
import { userLogin } from '../../../services/userLogin';
import { persistUserSession } from '../../../services/userSession';
import { save } from '../../../features/token/tokenSaver';
import { useDispatch } from 'react-redux';
import { isAmazonTestPhone, AMAZON_TEST_PHONE, AMAZON_TEST_CODE } from '../../../utils/amazonTestPhone';
import { prepareLoginPage } from '../../../services/prepareLoginPage';

export default function Login() {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState("");
    const [error, setError] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        prepareLoginPage();
    }, []);

    async function completeAmazonTestLogin() {
        const internationalPhone = phoneNumber.startsWith('+') ? phoneNumber : `+1${AMAZON_TEST_PHONE}`;
        const result = await userLogin({
            phoneNumber: internationalPhone,
            code: AMAZON_TEST_CODE,
        });

        dispatch(save(result.data.token));
        persistUserSession({
            token: result.data.token,
            refreshToken: result.data.refreshToken,
            phoneNumber: AMAZON_TEST_PHONE,
            userId: result.data.userId,
        });
        posthog.capture('user logged in', { method: 'amazon_test_phone' });
        navigate('/user/linkNewScreen/', {
            state: { phoneNumber: AMAZON_TEST_PHONE, token: result.data.token },
        });
    }

  async  function onSubmit(event) {
        event.preventDefault();
        setLoading(true);
        setError(false);

        const amazonTestPhone = isAmazonTestPhone(phoneNumber);
        if (!amazonTestPhone && !isValidPhoneNumber(phoneNumber)) {
            setLoading(false);
            setError(true);
            setErrorMessage('Please enter a valid phone number.');
            return;
        }

        if (amazonTestPhone) {
            try {
                await completeAmazonTestLogin();
            } catch (err) {
                setError(true);
                const status = err?.response?.status;
                const apiMessage = err?.response?.data?.message;
                if (status === 400 || status === 404) {
                    setErrorMessage('Amazon test login is not available on this server yet. Deploy the latest API, or use local dev (npm start).');
                    return;
                }
                setErrorMessage(apiMessage || 'Amazon test login failed. Please try again.');
            } finally {
                setLoading(false);
            }
            return;
        }

        if (isValidPhoneNumber(phoneNumber)) {
            await userExists(phoneNumber.replace('+1', '')).then((response) => {
                if (response.data.userExists) {
                    setLoading(false);
                    setError(false);
                    posthog.capture('user logged in', { method: 'phone' });
                    navigate("/userVerification", { state: { user: {phoneNumber: phoneNumber}, toRegister: false } })
                }
                else {
                    setLoading(false);
                    
                    navigate("/", { state: { phoneNumber: phoneNumber} })

                }
           }).catch((err) => {
                setLoading(false);
                const data = err.response?.data;
                if (data?.error === 'account_not_activated' || data?.error === 'account_inactive') {
                    setError(true);
                    let message = data.message || 'Unable to sign in with this phone number.';
                    if (data.useEmailLogin) {
                        message += ' You can sign in with email instead.';
                    }
                    setErrorMessage(message);
                    return;
                }
                setError(true);
                setErrorMessage('Something went wrong. Please try again.');
           })
        }
    }
    return (
        <div className='loginFlowPage'>
            <div className='loginFlowContainer'>
                <section className='loginFlowInfoPanel'>
                    <span className='loginFlowEyebrow'>Local-First Advertising Network</span>
                    <h1 className='loginFlowHeadline'>Welcome Back To INVERT</h1>
                    <p className='loginFlowDescription'>
                        Sign in with your phone number and we will verify your account by text so you can continue quickly.
                    </p>
                    <div className='loginFlowSteps'>
                        <div className='loginFlowStep'>
                            <span className='loginFlowStepNumber'>1</span>
                            <span className='loginFlowStepText'>Enter your mobile number</span>
                        </div>
                        <div className='loginFlowStep'>
                            <span className='loginFlowStepNumber'>2</span>
                            <span className='loginFlowStepText'>Verify with a one-time text code</span>
                        </div>
                        <div className='loginFlowStep'>
                            <span className='loginFlowStepNumber'>3</span>
                            <span className='loginFlowStepText'>Continue onboarding or return to dashboard</span>
                        </div>
                    </div>
                </section>

                <section className='loginFlowFormPanel'>
                    <form className='loginFlowForm' onSubmit={onSubmit}>
                        <span className='loginFlowBrand'>INVERT</span>
                        <h2 className='loginFlowTitle'>Log In</h2>
                        <p className='loginFlowSubTitle'>Use the number tied to your account to continue.</p>

                        <label className='loginFlowInputLabel' htmlFor='login-phone'>Mobile Phone Number</label>
                        <PhoneInput
                            id='login-phone'
                            className='loginphoneNumberInput'
                            country='US'
                            required
                            defaultCountry='US'
                            national={'false'}
                            value={phoneNumber}
                            onChange={setPhoneNumber}
                            placeholder='(555) 000-0000'
                        />

                        {error && <span className='errorPhoneVerification'>{errorMessage}</span>}

                        <button className='loginFlowSubmitButton' type='submit' disabled={loading}>
                            {!loading && 'Send Verification Code'}
                            {loading &&
                                <span className='loginRingLoaderContainer'>
                                    <RingLoader
                                        color={'#ffffff'}
                                        loading={true}
                                        size={20}
                                        aria-label='Loading Spinner'
                                        data-testid='loader'
                                    />
                                </span>
                            }
                        </button>

                        <p className='loginFlowFooter'>
                            New to INVERT? Enter your phone number and we will guide you through setup.
                            {' '}
                            <Link to="/screenHostLogin/">Screen host email login</Link>
                        </p>
                    </form>
                </section>
            </div>
        </div>
    )
}
